import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getBrokerDuplicates,
  getBrokerDuplicatesSummary,
  getCanonicalMatchCandidates,
  resolveCanonicalMatchCandidate,
  updateBrokerDuplicateStatus,
} from "../../../services/midpointApi";
import AdminStatePanel from "./layout/AdminStatePanel";

const PAIR_STATUS_OPTIONS = [
  { id: "SAME_PROPERTY", label: "Same Property", variant: "primary" },
  { id: "NOT_DUPLICATE", label: "Not Duplicate", variant: "default" },
  { id: "NEEDS_REVIEW", label: "Needs Review", variant: "default" },
  { id: "IGNORED", label: "Ignore", variant: "danger" },
];

const MATCH_ACTIONS = [
  { id: "CONFIRM_MATCH", label: "Confirm Match", variant: "primary" },
  { id: "CREATE_NEW_CANONICAL", label: "Create New Canonical", variant: "default" },
  { id: "REJECT_MATCH", label: "Reject Match", variant: "danger" },
  { id: "IGNORE", label: "Ignore", variant: "default" },
];

function ListingSide({ listing, label }) {
  if (!listing) {
    return <p className="muted">Listing unavailable</p>;
  }

  return (
    <div className="admin-dup-dash__listing">
      <span className="admin-dash__kpi-label">{label}</span>
      <strong>{listing.title}</strong>
      <p className="muted">
        {listing.displaySource} · {listing.source}
      </p>
      <p>
        {listing.price != null ? `${listing.price} ${listing.currencyCode || ""}` : "—"}
        {listing.areaM2 != null ? ` · ${listing.areaM2} m²` : ""}
      </p>
      <p className="muted">
        {[listing.neighborhood, listing.city].filter(Boolean).join(", ")}
        {listing.bedrooms != null ? ` · ${listing.bedrooms} bed` : ""}
        {listing.bathrooms != null ? ` · ${listing.bathrooms} bath` : ""}
      </p>
      {listing.detailUrl && (
        <a href={listing.detailUrl} target="_blank" rel="noopener noreferrer">
          View source →
        </a>
      )}
    </div>
  );
}

function SignalList({ signals }) {
  if (!signals || !Object.keys(signals).length) {
    return null;
  }

  return (
    <div className="admin-dup-dash__signals">
      {Object.entries(signals).map(([key, value]) => (
        <span key={key} className="admin-dup-dash__signal-tag">
          {key}: {String(value)}
        </span>
      ))}
    </div>
  );
}

const DuplicateReviewPage = ({ onClose }) => {
  const [viewMode, setViewMode] = useState("canonical");
  const [matchItems, setMatchItems] = useState([]);
  const [pairItems, setPairItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("NEEDS_REVIEW");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notes, setNotes] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [matchData, pairData, summaryData] = await Promise.all([
        getCanonicalMatchCandidates({
          status: statusFilter || undefined,
          limit: 100,
        }),
        getBrokerDuplicates({ status: viewMode === "pairs" ? statusFilter || "CANDIDATE" : undefined, limit: 50 }),
        getBrokerDuplicatesSummary().catch(() => null),
      ]);

      setMatchItems(matchData.items || []);
      setPairItems(pairData.items || []);
      setSummary(summaryData);
    } catch (loadError) {
      setMatchItems([]);
      setPairItems([]);
      setSummary(null);
      setError(loadError.message || "Unable to load duplicate review queue.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, viewMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resolveMatch = async (candidateId, action) => {
    try {
      setActionError("");
      await resolveCanonicalMatchCandidate(candidateId, { action, notes: notes[candidateId] || null });
      setNotes((prev) => {
        const next = { ...prev };
        delete next[candidateId];
        return next;
      });
      await loadData();
    } catch (resolveError) {
      setActionError(resolveError.message || "Unable to resolve duplicate candidate.");
    }
  };

  const updatePairStatus = async (candidateId, status) => {
    try {
      setActionError("");
      await updateBrokerDuplicateStatus(candidateId, { status });
      await loadData();
    } catch (statusError) {
      setActionError(statusError.message || "Unable to update duplicate status.");
    }
  };

  const duplicateStats = summary?.duplicates || {};

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Pair Candidates", value: duplicateStats.candidates ?? 0 },
      { label: "High Confidence", value: duplicateStats.high_confidence ?? 0, tone: (duplicateStats.high_confidence ?? 0) > 0 ? "green" : null },
      { label: "Same Property", value: duplicateStats.same_property ?? 0, tone: "green" },
      { label: "Canonical Matches", value: matchItems.length },
      { label: "Pair Items", value: pairItems.length },
    ];
  }, [summary, duplicateStats, matchItems.length, pairItems.length]);

  const activeItems = viewMode === "canonical" ? matchItems : pairItems;

  return (
    <div className="admin-dashboard admin-dup-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Duplicate Review</h1>
          <p className="admin-dashboard__subtitle">
            Review medium-confidence canonical matches before they appear as separate map properties
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadData}
            disabled={loading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!loading && !error && summary && (
        <section className="admin-dashboard__kpis">
          {kpis.map((kpi) => (
            <div className="admin-dash__kpi-card" key={kpi.label}>
              <span className="admin-dash__kpi-label">{kpi.label}</span>
              <span
                className={`admin-dash__kpi-value${
                  kpi.tone === "green"
                    ? " admin-dash__kpi-value--green"
                    : kpi.tone === "red"
                      ? " admin-dash__kpi-value--red"
                      : kpi.tone === "amber"
                        ? " admin-dash__kpi-value--amber"
                        : ""
                }`}
              >
                {kpi.value}
              </span>
            </div>
          ))}
        </section>
      )}

      {actionError && (
        <div className="admin-re-toast admin-re-toast--error" role="alert">
          {actionError}
        </div>
      )}

      <section className="admin-dash__card admin-dash__card--wide admin-dup-dash__queue-card">
        <div className="admin-dup-dash__toolbar">
          <div className="admin-dup-dash__filters">
            <select
              className="admin-source-controls-dash__select"
              value={viewMode}
              onChange={(event) => setViewMode(event.target.value)}
            >
              <option value="canonical">Source → Canonical Matches</option>
              <option value="pairs">Legacy Pair Candidates</option>
            </select>
            <select
              className="admin-source-controls-dash__select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="NEEDS_REVIEW">Needs review</option>
              <option value="AUTO_MATCHED">Auto matched</option>
              <option value="CONFIRMED_MATCH">Confirmed</option>
              <option value="REJECTED_MATCH">Rejected</option>
              <option value="IGNORED">Ignored</option>
              <option value="">All</option>
            </select>
          </div>
          <span className="admin-geocode-dash__item-count">
            {activeItems.length} item{activeItems.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? <AdminStatePanel variant="loading" message="Loading duplicate review…" /> : null}

        {!loading && error ? (
          <AdminStatePanel variant="error" message={error} onRetry={loadData} />
        ) : null}

        {!loading && !error && activeItems.length === 0 ? (
          <div className="admin-geocode-dash__empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
            <p className="muted">No duplicate candidates match the current filters.</p>
          </div>
        ) : null}

        {!loading && !error && viewMode === "canonical" && matchItems.length > 0 ? (
          <div className="admin-dup-dash__list">
            {matchItems.map((item) => (
              <article key={item.id} className="admin-dup-dash__item">
                <div className="admin-dup-dash__item-header">
                  <div className="admin-dup-dash__score">
                    <span className="admin-dup-dash__score-value">{item.matchScore}</span>
                    <span className="admin-dash__kpi-label">score</span>
                  </div>
                  <div className="admin-dup-dash__item-status">
                    <span className="admin-canonical-dash__status-pill">
                      {item.matchStatus}
                    </span>
                    {item.matchDecision && (
                      <span className="muted">{item.matchDecision}</span>
                    )}
                  </div>
                </div>

                <p className="admin-review-dash__reason">{item.matchReason}</p>
                {item.suggestedAction && (
                  <p className="admin-review-dash__suggestion">{item.suggestedAction}</p>
                )}

                <SignalList signals={item.matchSignals} />

                <div className="admin-dup-dash__comparison">
                  <ListingSide
                    listing={{
                      title: item.sourceListing?.title,
                      source: item.sourceKey,
                      displaySource: item.sourceListing?.displaySource || item.sourceKey,
                      price: item.sourceListing?.price,
                      currencyCode: item.sourceListing?.currencyCode,
                      areaM2: item.sourceListing?.areaM2,
                      bedrooms: item.sourceListing?.bedrooms,
                      bathrooms: item.sourceListing?.bathrooms,
                      neighborhood: item.sourceListing?.neighborhood,
                      city: item.sourceListing?.city,
                      detailUrl: item.sourceListing?.sourceUrl,
                    }}
                    label="Source Listing"
                  />
                  <div className="admin-dup-dash__listing">
                    <span className="admin-dash__kpi-label">Canonical Property</span>
                    <strong>{item.canonicalProperty?.title}</strong>
                    <p>
                      {item.canonicalProperty?.priceMin != null
                        ? `${item.canonicalProperty.priceMin} – ${item.canonicalProperty.priceMax} ${item.canonicalProperty.currency || ""}`
                        : "—"}
                    </p>
                    <p className="muted">
                      {[item.canonicalProperty?.neighborhood, item.canonicalProperty?.city]
                        .filter(Boolean)
                        .join(", ")}
                      {item.canonicalProperty?.activeSourceCount != null
                        ? ` · ${item.canonicalProperty.activeSourceCount} active source(s)`
                        : ""}
                    </p>
                  </div>
                </div>

                <div className="admin-dup-dash__resolve">
                  <textarea
                    className="admin-review-dash__notes"
                    placeholder="Notes (optional)"
                    value={notes[item.id] || ""}
                    onChange={(event) =>
                      setNotes((prev) => ({ ...prev, [item.id]: event.target.value }))
                    }
                    rows={2}
                  />
                  <div className="admin-review-dash__actions">
                    {MATCH_ACTIONS.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        className={`admin-dashboard__action-btn${
                          action.variant === "primary"
                            ? " admin-dashboard__action-btn--primary"
                            : action.variant === "danger"
                              ? " admin-canonical-dash__action-danger"
                              : ""
                        }`}
                        onClick={() => resolveMatch(item.id, action.id)}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {!loading && !error && viewMode === "pairs" && pairItems.length > 0 ? (
          <div className="admin-dup-dash__list">
            {pairItems.map((item) => (
              <article key={item.id} className="admin-dup-dash__item">
                <div className="admin-dup-dash__item-header">
                  <div className="admin-dup-dash__score">
                    <span className="admin-dup-dash__score-value">{item.matchScore}</span>
                    <span className="admin-dash__kpi-label">score</span>
                  </div>
                  <span className="admin-canonical-dash__status-pill">
                    {item.status}
                  </span>
                </div>

                {(item.matchReasons || []).length > 0 && (
                  <div className="admin-dup-dash__signals">
                    {item.matchReasons.map((reason) => (
                      <span key={reason} className="admin-dup-dash__signal-tag">
                        {reason.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                )}

                <div className="admin-dup-dash__comparison">
                  <ListingSide listing={item.listingA} label="Listing A" />
                  <ListingSide listing={item.listingB} label="Listing B" />
                </div>

                <div className="admin-review-dash__actions">
                  {PAIR_STATUS_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`admin-dashboard__action-btn${
                        option.variant === "primary"
                          ? " admin-dashboard__action-btn--primary"
                          : option.variant === "danger"
                            ? " admin-canonical-dash__action-danger"
                            : ""
                      }`}
                      onClick={() => updatePairStatus(item.id, option.id)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default DuplicateReviewPage;
